import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Mail, Phone, MapPin, Calendar, FileText, User, Wrench,
  Bot, Sparkles, AlertTriangle, Home, Clock, KeyRound, MessageSquare, Hash,
} from 'lucide-react';
import { enquiriesAPI } from '@/utils/api';
import { toast } from 'sonner';

// Status values that exactly match the backend Enquiry enum
const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'quoted', label: 'Quoted' },
  { value: 'booked', label: 'Booked' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

// Safe formatters — never throw on null/undefined input
const fallback = (v) => {
  if (v === null || v === undefined) return 'Not provided';
  if (typeof v === 'string' && !v.trim()) return 'Not provided';
  return v;
};

const formatDate = (iso) => {
  if (!iso) return 'Not provided';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return 'Not provided';
    return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return 'Not provided';
  }
};

const prettyService = (s) => {
  if (!s) return 'Unspecified';
  return String(s).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const InfoRow = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-start space-x-3" data-testid={testId}>
    <Icon className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
    <div className="min-w-0">
      <p className="text-gray-400 text-xs uppercase tracking-wide">{label}</p>
      <p className="text-white break-words">{fallback(value)}</p>
    </div>
  </div>
);

const EnquiryDetailModal = ({ enquiry, open, onClose, onUpdated }) => {
  // Guard: never try to read off a null enquiry. List page already gates on truthy,
  // but defence in depth avoids blank-screen crashes if a parent ever misbehaves.
  const [status, setStatus] = useState(enquiry?.status || 'new');
  const [notes, setNotes] = useState(enquiry?.internal_notes || '');
  const [updating, setUpdating] = useState(false);

  if (!enquiry) return null;

  const isAICall = enquiry.source === 'ai_receptionist';
  const isEmergency = enquiry.urgency === 'emergency' || enquiry.emergency_flag === true;

  const handleUpdateStatus = async () => {
    try {
      setUpdating(true);
      await enquiriesAPI.updateStatus(enquiry.enquiry_id, status);
      toast.success('Status updated successfully');
      onUpdated?.();
    } catch (error) {
      toast.error(error?.message || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateNotes = async () => {
    try {
      setUpdating(true);
      await enquiriesAPI.update(enquiry.enquiry_id, { internal_notes: notes });
      toast.success('Notes updated successfully');
      onUpdated?.();
    } catch (error) {
      toast.error(error?.message || 'Failed to update notes');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        data-testid="enquiry-detail-modal"
        className="max-w-3xl bg-gray-900 border-gray-800 text-white max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-3">
            Enquiry Details
            {isAICall && (
              <Badge className="bg-indigo-500/20 text-indigo-300 border border-indigo-500/40 gap-1.5" data-testid="badge-ai-receptionist">
                <Bot className="w-3.5 h-3.5" /> AI Receptionist
              </Badge>
            )}
            {isEmergency && (
              <Badge className="bg-red-500/20 text-red-300 border border-red-500/40 gap-1.5" data-testid="badge-emergency">
                <AlertTriangle className="w-3.5 h-3.5" /> EMERGENCY
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Customer Info */}
          <div className="bg-gray-800/50 rounded-lg p-4 space-y-3">
            <h3 className="font-semibold text-lg">Customer Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoRow icon={User}     label="Name"       value={enquiry.customer_name}  testId="detail-customer-name" />
              <InfoRow icon={Mail}     label="Email"      value={enquiry.customer_email} testId="detail-customer-email" />
              <InfoRow icon={Phone}    label="Phone"      value={enquiry.customer_phone} testId="detail-customer-phone" />
              <InfoRow icon={Calendar} label="Submitted"  value={formatDate(enquiry.created_at)} testId="detail-created-at" />
            </div>
          </div>

          {/* Enquiry Details */}
          <div className="space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="font-semibold text-lg">Enquiry Details</h3>
              <div className="flex items-center space-x-2 flex-wrap">
                <Badge className="bg-blue-500/20 text-blue-300 border border-blue-500/40 capitalize gap-1.5">
                  <Wrench className="w-3.5 h-3.5" /> {prettyService(enquiry.service_type)}
                </Badge>
                <Badge
                  className={
                    isEmergency
                      ? 'bg-red-500/20 text-red-300 border border-red-500/40'
                      : enquiry.urgency === 'urgent'
                        ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                        : 'bg-gray-600/30 text-gray-300 border border-gray-600/40'
                  }
                >
                  {fallback(enquiry.urgency)}
                </Badge>
                <Badge className="bg-gray-700/40 text-gray-300 border border-gray-600/40 capitalize">
                  Source: {(enquiry.source || 'unknown').replace(/_/g, ' ')}
                </Badge>
              </div>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-4">
              <p className="text-gray-300 whitespace-pre-wrap" data-testid="detail-issue-description">
                {fallback(enquiry.issue_description)}
              </p>
            </div>
          </div>

          {/* Location — always render, even if empty */}
          <div className="bg-gray-800/50 rounded-lg p-4 space-y-3">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <MapPin className="w-5 h-5 text-amber-500" /> Location & Property
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoRow icon={MapPin} label="Location"      value={enquiry.location_text || enquiry.location} testId="detail-location" />
              <InfoRow icon={Hash}   label="Postcode"      value={enquiry.postcode} testId="detail-postcode" />
              <InfoRow icon={Home}   label="Property Type" value={enquiry.property_type} testId="detail-property-type" />
              <InfoRow icon={Clock}  label="Preferred Time" value={enquiry.preferred_time} testId="detail-preferred-time" />
              <InfoRow icon={KeyRound}     label="Access Notes"       value={enquiry.access_notes} testId="detail-access-notes" />
              <InfoRow icon={MessageSquare} label="Contact Preference" value={enquiry.contact_preference} testId="detail-contact-pref" />
            </div>
          </div>

          {/* AI Receptionist section — only for ai_receptionist records */}
          {isAICall && (
            <div
              className="rounded-lg p-4 space-y-3 border border-indigo-500/30 bg-gradient-to-br from-indigo-500/5 to-gray-900/0"
              data-testid="detail-ai-section"
            >
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-indigo-400" /> AI Receptionist Data
              </h3>

              <InfoRow icon={Hash} label="Vapi Call ID" value={enquiry.vapi_call_id} testId="detail-vapi-call-id" />

              <div>
                <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">AI Summary</p>
                <p
                  className="text-gray-200 whitespace-pre-wrap bg-gray-900/60 rounded-md p-3 text-sm"
                  data-testid="detail-ai-summary"
                >
                  {fallback(enquiry.ai_summary)}
                </p>
              </div>

              <div>
                <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">Transcript</p>
                {enquiry.transcript ? (
                  <pre
                    className="text-gray-300 whitespace-pre-wrap bg-gray-900/60 rounded-md p-3 text-xs max-h-64 overflow-y-auto font-mono leading-relaxed"
                    data-testid="detail-transcript"
                  >
                    {enquiry.transcript}
                  </pre>
                ) : (
                  <p className="text-gray-500 italic text-sm" data-testid="detail-transcript-empty">
                    No transcript captured.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Status Update */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Update Status</h3>
            <div className="flex items-center space-x-3">
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger
                  className="flex-1 bg-gray-800 border-gray-700 text-white"
                  data-testid="detail-status-trigger"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  {STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} data-testid={`status-option-${opt.value}`}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={handleUpdateStatus}
                disabled={updating || status === enquiry.status}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="detail-update-status-btn"
              >
                Update Status
              </Button>
            </div>
          </div>

          {/* Internal Notes */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-500" /> Internal Notes
            </h3>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add internal notes about this enquiry..."
              className="bg-gray-800 border-gray-700 text-white min-h-[100px]"
              data-testid="detail-notes-input"
            />
            <Button
              onClick={handleUpdateNotes}
              disabled={updating}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="detail-save-notes-btn"
            >
              Save Notes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EnquiryDetailModal;
